import { useTranslations } from "use-intl";

import { FallbackImage } from "@/components/fallback-image";
import { UserAvatar } from "@/components/feed/feed-event-primitives";
import { DealNotificationDetails } from "@/components/notifications/deal-notification-details";
import {
  NotificationMainLine,
  notificationActorDisplayLabel,
} from "@/components/notifications/notification-main-line";
import { NotificationSubjectDetails } from "@/components/notifications/notification-subject-details";
import { cn } from "@/lib/utils";
import type { ClientNotificationItem } from "@garimpa/api/notifications/toClientNotification";

function notificationIcon(type: string): AppIcon {
  const icons: Record<string, AppIcon> = {
    new_follower: FollowIcon,
    post_like: FavoriteIcon,
    feed_event_comment: MessageIcon,
    deal_price_drop: TrendingDownIcon,
  };
  return icons[type] ?? NotificationIcon;
}

function isCatalogNotification(type: string): boolean {
  return type === "deal_price_drop";
}

export function NotificationItemContent({
  notification,
}: {
  notification: ClientNotificationItem;
}) {
  const t = useTranslations("globals.notifications");
  const { thumbUrl } = notification.subject;
  const catalogNotification = isCatalogNotification(notification.type);
  const showThumb = catalogNotification && Boolean(thumbUrl);
  const actorName = notificationActorDisplayLabel(notification.actor, t("someone"));
  const Icon = notificationIcon(notification.type);

  return (
    <>
      <Icon className="text-primary text-[20px] mt-0.5 shrink-0" />
      {showThumb ? (
        <div className="relative size-10 shrink-0 rounded-md overflow-hidden bg-secondary">
          <FallbackImage src={thumbUrl} alt="" className="object-cover" sizes="40px" />
        </div>
      ) : (
        <UserAvatar image={notification.actor?.avatarUrl ?? null} name={actorName} size={36} />
      )}
      <div className="flex-1 min-w-0">
        <p
          className={cn(
            "text-sm",
            catalogNotification && "line-clamp-2",
            !notification.read && "font-medium",
          )}
        >
          <NotificationMainLine notification={notification} />
        </p>
        {catalogNotification ? (
          <DealNotificationDetails notification={notification} />
        ) : (
          <NotificationSubjectDetails notification={notification} />
        )}
      </div>
      {!notification.read ? (
        <span className="size-2 rounded-full bg-primary shrink-0 mt-2" />
      ) : null}
    </>
  );
}
import {
  FavoriteIcon,
  FollowIcon,
  MessageIcon,
  NotificationIcon,
  TrendingDownIcon,
} from "@/components/appIcons";
import type { AppIcon } from "@/components/appIcons";
